import React, { FC, useContext, useState } from "react";
import { useRouter } from "next/router";
import { ApolloError } from "@apollo/client";
import { GraphQLError } from "graphql";
import { AppContext } from "context/app";
import { ModalContext } from "context/modal";
import { UserContext } from "context/user";
import { useRouterWithRedirect } from "hooks/useRouterWithRedirect";
import { LoginForm as PepLoginForm } from "@pepdirect/v3/ui/LoginForm";
import { logEvent } from "@pepdirect/v3/helpers/analyticsLogger";
import { parseLoginUserGraphQLErrors } from "@pepdirect/v3/helpers/errors";
import { UpdatePassword } from "@pepdirect/v3/ui/UpdatePassword";
import { endpoints } from "config";
import { EVENT_NAMES } from "@pepdirect/v3/types";
import {
  useLoginMutation,
  LoginMutationVariables,
} from "services/graphql/generated";
import { routes } from "constants/serviceRoutes";

interface LoginFormProps {
  inModal?: boolean;
}

export const LoginForm: FC<LoginFormProps> = ({ inModal = false }) => {
  const { tenant } = useContext(AppContext);
  const { open, close } = useContext(ModalContext);
  const { setUser } = useContext(UserContext);

  const [mappedErrorMessage, setMappedErrorMessage] = useState("");
  const [showUpdatePasswordScreen, setShowUpdatePasswordScreen] =
    useState(false);

  const [loginMutation, { loading }] = useLoginMutation();
  const { pushPreserveRedirect } = useRouterWithRedirect();
  const router = useRouter();

  const onSubmit = async ({ email, password }: LoginMutationVariables) => {
    setMappedErrorMessage("");
    try {
      const { data } = await loginMutation({
        variables: { email, password },
      });
      const userId = data?.loginUser?.id || null;
      setUser(userId);
      logEvent(userId, EVENT_NAMES.userLoginSuccess);
      inModal ? close() : router.push(routes.postLoginRedirect);
    } catch (e) {
      if (e instanceof ApolloError && e.graphQLErrors.length) {
        const { errorMsg, passwordResetRequired } = parseLoginUserGraphQLErrors(
          e.graphQLErrors as GraphQLError[]
        );

        if (passwordResetRequired) {
          setShowUpdatePasswordScreen(true);
        } else {
          setMappedErrorMessage(errorMsg);
        }
      }
    }
  };

  const goToForgotPassword = () => {
    if (inModal) {
      open("FORGOT_PASSWORD_MODAL");
    } else {
      close();
      pushPreserveRedirect("/forgot-password");
    }
  };

  if (showUpdatePasswordScreen) {
    return <UpdatePassword />;
  }

  return (
    <PepLoginForm
      customTexts={tenant?.brandConfig.customTexts}
      error={mappedErrorMessage || ""}
      goToForgotPassword={goToForgotPassword}
      isLoading={loading}
      logoSrc={`${endpoints.cdn}/static-images/gatorade-icons/teamzone-logo-black.svg`}
      onSubmit={onSubmit}
      tenantName={tenant?.brandConfig.title}
    />
  );
};
